// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../constants/api";
import Bell from "../components/notifications/Bell.jsx";
import NotificationList from "../components/notifications/List.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import { tw, cn } from "../utils/themeClasses.js";
import TopbarPendenzen from "../components/Pendenzen/TopbarPendenzen.jsx";
import DashboardTopBarKpis from "../components/Dashboard/DashboardTopBarKpis.jsx";
import GlobalSearch from "../components/ui/GlobalSearch.jsx";
import ElektroMannIcon from "../components/ui/ElektroMannIcon.jsx";
import WelcomeWisdomDaily from "../components/Dashboard/WelcomeWisdomDaily.jsx";
import { pickTimeOfDay, greetingFor, msUntilNextBoundary } from "../public/utils/timeOfDay.js";
import { promise } from "../utils/toast.js";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* ---------- Widget-System ---------- */
import WidgetLayout from "../components/widgets/WidgetLayout.jsx";
import { DashboardOverviewProvider } from "../context/DashboardOverviewContext.jsx";

/* ---------- Rollen-Helfer ---------- */
import { canAccessArchive, canAccessCustomers, canAccessInventory, canAccessWartung, canSeeMgmt, canSeeLeitstand, prettyRole } from "../constants/roles.js";
import {
  shouldShowDashboardVerkaufTile,
  shouldShowDashboardLegacyOffertenTile,
} from "../constants/salesRoles.js";
import { useAuth } from "../components/ui/AuthContext.jsx";
import { useAcademyContextEvent } from "../hooks/useAcademyContextEvent.js";

function normalizeQuickAccessOrder(tiles, rawOrder) {
  const fallback = tiles.map(t => t.path);
  if (!Array.isArray(rawOrder) || rawOrder.length === 0) return fallback;
  const known = new Set(fallback);
  const filtered = rawOrder.filter(path => typeof path === "string" && known.has(path));
  const missing = fallback.filter(path => !filtered.includes(path));
  return [...filtered, ...missing];
}
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loadingAuth, signOut, hasFeature } = useAuth();
  useAcademyContextEvent("visit_dashboard");
  const [authError, setAuthError] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [editQuickAccess, setEditQuickAccess] = useState(false);
  const [quickAccessOrder, setQuickAccessOrder] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (loadingAuth) return;
    if (!user) {
      setAuthError("Sitzung abgelaufen oder nicht angemeldet.");
      navigate("/login", { replace: true });
    } else {
      setAuthError(null);
    }
  }, [loadingAuth, user, navigate]);

  // dynamischer Gruß (aktualisiert sich stündlich)
  const [greet, setGreet] = useState(() => greetingFor(pickTimeOfDay()));
  useEffect(() => {
    let timer;
    function step() {
      setGreet(greetingFor(pickTimeOfDay()));
      timer = setTimeout(step, msUntilNextBoundary());
    }
    step();
    return () => clearTimeout(timer);
  }, []);

  /* --------- misc setup --------- */
  useEffect(() => {
    document.title = "Dashboard • WireTrack";
    setIsTouch(typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0));
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleLogout = useCallback(async () => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    try {
      await promise(signOut(), {
        loading: "Melde ab…",
        success: "Abgemeldet.",
        error: "Abmelden fehlgeschlagen."
      });
    } finally {
      try {
        localStorage.removeItem("wrtk.passkey.nudged.v1");
      } catch {
        /* ignore */
      }
      navigate("/login", {
        replace: true
      });
    }
  }, [logoutBusy, navigate, signOut]);
  /* --------- Keyboard Shortcuts (nur Desktop) --------- */
  useEffect(() => {
    if (isTouch) return;
    const onKey = e => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const typing = tag === "input" || tag === "textarea" || document.activeElement?.isContentEditable;
      if (!typing && (e.key === "l" || e.key === "L")) {
        e.preventDefault();
        handleLogout();
      }
      if (!typing && (e.key === "b" || e.key === "B")) {
        e.preventDefault();
        setNotifOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isTouch, handleLogout]);

  /* Admin / Management (Rollen 1–4) */
  const canMgmt = useMemo(() => canSeeMgmt(user), [user]);
  /* Leitstand (Rollen 1–4) – PL darf rein */
  const canLeitstand = useMemo(() => canSeeLeitstand(user), [user]);
  const canCustomers = useMemo(() => canAccessCustomers(user), [user]);
  const canInventory = useMemo(() => canAccessInventory(user), [user]);
  const canOffers = useMemo(
    () => shouldShowDashboardLegacyOffertenTile(user, hasFeature),
    [user, hasFeature]
  );
  const canSales = useMemo(
    () => shouldShowDashboardVerkaufTile(user, hasFeature),
    [user, hasFeature]
  );
  const canArchive = useMemo(() => canAccessArchive(user), [user]);
  const canWartung = useMemo(() => canAccessWartung(user), [user]);

  /* ---------- Kacheln – gewünschte Reihenfolge ---------- */
  const SHOW_HELP_TILE = true;
  const tiles = useMemo(() => {
    const out = [
    // ───── Block 1: Persönlich & Zeit ─────
    {
      label: "🧾 Meine Aufgaben",
      path: "/meine-aufgaben"
    }, {
      label: "📅 Kalender",
      path: "/kalender"
    },
    // ───── Block 2: Leitstand / Monitoring ─────
    ...(canLeitstand ? [{
      label: "📊 Übersicht",
      path: "/leitstand/alle"
    }] : []), ...(canMgmt ? [{
      label: "🔭 Monitoring",
      path: "/monitoring/pva"
    }] : []),
    // ───── Block 3: Aufträge / Tickets ─────
    {
      label: "🛠️ Serviceaufträge",
      path: "/service"
    }, ...(canWartung ? [{
      label: "🧰 Wartungsverträge",
      path: "/wartungsvertraege"
    }] : []), {
      label: "📝 Mängelberichte",
      path: "/maengel"
    },
    // ───── Block 4: Projekte ─────
    {
      label: "⚡ Projekte Elektro",
      path: "/projekte/elektro"
    }, {
      label: "🔋 Projekte PV",
      path: "/projekte/pv"
    }, {
      label: "🛡️ Projekte Sicherheit",
      path: "/projekte/sicherheit"
    }, {
      label: "🔌 Projekte Ladestationen",
      path: "/projekte/ladestation"
    }, {
      label: "🏠 Projekte Intern",
      path: "/projekte/intern"
    }, {
      label: "🛍️ Projekte Ladenbau",
      path: "/projekte/ladenbau"
    },
    // ───── Block 5: Stammdaten / Ressourcen ─────
    ...(canCustomers ? [{
      label: "👥 Kunden",
      path: "/kunden"
    }] : []), ...(canInventory ? [{
      label: "📦 Inventar",
      path: "/inventar"
    }] : []),
    // ───── Block 6: Meta ─────
    ...(canOffers ? [{
      label: "📄 Offerten",
      path: "/offerten"
    }] : []),     ...(canSales ? [{
      label: "🧾 Verkauf",
      path: "/verkauf"
    }] : []), ...(canArchive ? [{
      label: "🗃️ Archiv",
      path: "/archiv"
    }] : []), {
      label: "🙍 Mein Profil",
      path: "/profil"
    },
    // Admin nur für berechtigte Rollen (Management)
    ...(canMgmt ? [{
      label: "🛡️ Admin / GL / Büro",
      path: "/admin"
    }] : [])];
    if (SHOW_HELP_TILE) out.push({
      label: "WireHub",
      path: "/hilfe",
      icon: "elektromann"
    });
    return out;
  }, [canArchive, canCustomers, canInventory, canLeitstand, canMgmt, canOffers, canSales, canWartung]);
  useEffect(() => {
    let alive = true;
    const applyOrder = order => {
      if (!alive) return;
      setQuickAccessOrder(normalizeQuickAccessOrder(tiles, order));
    };
    (async () => {
      try {
        const res = await api.get("/profil/widgets", {
          silent: true
        });
        applyOrder(res?.data?.config?.widgets?.quick_access_order);
      } catch {
        applyOrder(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [tiles, user?.id]);
  const saveQuickAccessOrder = useCallback(async order => {
    try {
      await api.patch("/profil/widgets", {
        widgets: {
          quick_access_order: order
        }
      }, {
        silent: true
      });
    } catch {
      // ohne Blocking weitermachen
    }
  }, []);
  const orderedTiles = useMemo(() => {
    const byPath = new Map(tiles.map(t => [t.path, t]));
    const normalized = normalizeQuickAccessOrder(tiles, quickAccessOrder);
    return normalized.map(path => byPath.get(path)).filter(Boolean);
  }, [tiles, quickAccessOrder]);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 6
    }
  }));
  const handleQuickAccessDragEnd = useCallback(event => {
    const {
      active,
      over
    } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;
    setQuickAccessOrder(prev => {
      const current = normalizeQuickAccessOrder(tiles, prev);
      const oldIndex = current.indexOf(active.id);
      const newIndex = current.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return current;
      const next = arrayMove(current, oldIndex, newIndex);
      void saveQuickAccessOrder(next);
      return next;
    });
  }, [saveQuickAccessOrder, tiles]);
  const RoleBadge = ({
    rolle
  }) => rolle ? <span className={cn("text-[10px] sm:text-[11px] px-2 py-1 rounded-full border", tw.badge(true))}>
        {rolle}
      </span> : null;
  const roleDisplay = useMemo(() => prettyRole(user?.rolle) || "", [user]);
  const Tile = ({
    label,
    path,
    icon,
    dragHandleProps
  }) => <Link to={path} onClick={editQuickAccess ? e => e.preventDefault() : undefined} {...editQuickAccess ? dragHandleProps : {}} className={cn("group block w-full h-24 sm:h-28 rounded-2xl border", "shadow-sm transition-all duration-200 motion-reduce:transition-none", "md:hover:-translate-y-0.5", "focus:outline-none focus:ring-2 focus:ring-blue-400/40", "relative overflow-hidden min-h-[96px]", "active:scale-[0.98] md:active:scale-100", tw.card(true), tw.cardHover(true), editQuickAccess ? "cursor-grab select-none" : "")} style={editQuickAccess ? {
    animation: "wrtk-quick-wiggle 220ms ease-in-out infinite alternate"
  } : undefined} title={label} aria-label={label}>
      <div className={cn("pointer-events-none absolute inset-0 opacity-0 md:group-hover:opacity-100 transition-opacity duration-200", "")} style={{
      boxShadow: "inset 0 0 60px rgba(59,130,246,0.15)"
    }} aria-hidden="true" />
      <div className="h-full w-full grid place-items-center px-2">
        <span className="inline-flex flex-col items-center gap-0.5 text-center">
          <span className="inline-flex items-center gap-2">
            {icon === "elektromann" && <ElektroMannIcon className="h-5 w-3.5 shrink-0" />}
            <span className={cn("text-[13px] sm:text-[15px] font-semibold leading-tight break-words hyphens-auto", tw.textSecondary(true))}>
              {label}
            </span>
          </span>
        </span>
      </div>
    </Link>;
  const SortableQuickTile = ({
    tile
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: tile.path,
      disabled: !editQuickAccess
    });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition: transition || "transform 180ms ease"
    };
    return <div ref={setNodeRef} style={style} className={cn("relative", isDragging ? "opacity-70 z-20" : "")}>
        {editQuickAccess && <span className={cn("absolute left-2 top-2 z-20 px-1.5 py-0.5 rounded text-[10px] border pointer-events-none", "bg-black/50 text-slate-200 border-white/20")}>
            Ziehen
          </span>}
        <Tile {...tile} dragHandleProps={{
        ...attributes,
        ...listeners
      }} />
      </div>;
  };
  const showMock = false;
  // Dashboard Content (wird von WidgetLayout gewrapped auf Desktop)
  const dashboardContent = <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] px-3 sm:px-4 py-4 sm:py-6">
      <div className="mx-auto max-w-5xl">
          {authError ? <div className="mb-4 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">{authError}</div> : null}
          {/* Topbar (Glass) */}
          <div className={cn("rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 mb-4 sm:mb-6 border relative z-30", tw.glass(true))}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className={cn("text-lg sm:text-xl font-bold tracking-tight", tw.textPrimary(true))}>
                  {user ? `${greet.text}, ${user.name}` : greet.text}
                  <span className="align-middle ml-2" aria-hidden="true">{greet.emoji}</span>
                </h1>
                <div className={cn("mt-1 flex items-center gap-2 text-[13px] sm:text-sm", tw.textMuted(true))}>
                  <span>WireTrack Dashboard</span>
                  {roleDisplay && <RoleBadge rolle={roleDisplay} />}
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <TopbarPendenzen currentUser={user} />

                <div className="relative z-50" data-notif-anchor>
                  <Bell onClick={() => setNotifOpen(v => !v)} open={notifOpen} />
                  <NotificationList open={notifOpen} onClose={() => setNotifOpen(false)} />
                </div>

                <AppButton
                  type="button"
                  variant="ghost"
                  onClick={handleLogout}
                  disabled={logoutBusy}
                  title="Abmelden (Taste L)"
                  className={cn(
                    "wrtk-logout-btn relative z-[1] inline-flex items-center justify-center rounded-xl",
                    "h-10 px-3 sm:px-3.5 min-h-[44px] md:min-h-0",
                    "text-[13px] sm:text-sm font-medium",
                    "focus:outline-none focus:ring-2 focus:ring-red-400/45 focus:ring-offset-0",
                    "disabled:opacity-55 disabled:cursor-not-allowed"
                  )}
                >
                  <span className="relative z-[1]">{logoutBusy ? "Abmelden…" : "Logout"}</span>
                </AppButton>
              </div>
            </div>

            {/* 🔍 Globale Suche */}
            <div className="mt-3">
              <GlobalSearch />
            </div>
          </div>

          {/* 👋 Tägliche Weisheit (pro User & Tag einmal) */}
          {user && <div className="mb-4 sm:mb-6">
              <WelcomeWisdomDaily user={user} />
            </div>}

          {/* KPIs */}
          <DashboardTopBarKpis />

          {/* Schnellzugriff */}
          <section>
            <style>{`
              @keyframes wrtk-quick-wiggle {
                0% { transform: rotate(-0.8deg); }
                100% { transform: rotate(0.8deg); }
              }
            `}</style>
            <div className="flex items-center justify-between mb-2.5 sm:mb-3 gap-2">
              <h2 className={cn("text-base sm:text-lg font-semibold", tw.heading(true))}>Schnellzugriff</h2>
              <AppButton type="button" variant="ghost" size="sm" onClick={() => setEditQuickAccess(v => !v)} className={cn("!min-h-0 h-auto px-1.5 py-0.5 text-[11px] sm:text-xs whitespace-nowrap font-normal rounded-md shrink-0", "!text-slate-400 hover:!text-slate-200")}>
                {editQuickAccess ? "Fertig" : "Reihenfolge anpassen"}
              </AppButton>
            </div>
            {editQuickAccess && <p className={cn("mb-2 text-xs", tw.textMuted(true))}>
                Kachel halten und auf eine andere ziehen, um die Reihenfolge zu ändern.
              </p>}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={editQuickAccess ? handleQuickAccessDragEnd : undefined}>
              <SortableContext items={orderedTiles.map(t => t.path)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {orderedTiles.map(t => <SortableQuickTile key={t.path} tile={t} />)}
                  {showMock && Array.from({
                length: 4
              }).map((_, i) => <div key={i} className={cn("h-24 sm:h-28 rounded-2xl border animate-pulse", tw.skeleton(true), "border-white/10")} aria-hidden="true" />)}
                </div>
              </SortableContext>
            </DndContext>
          </section>
        </div>
      </div>;

  // Return mit WidgetLayout wrapper
  const layout = <WidgetLayout>{dashboardContent}</WidgetLayout>;
  return (
    <div className={cn("min-h-screen w-full", tw.page(true), tw.textPrimary(true))}>
      <DashboardOverviewProvider enabled={!!user}>
        {layout}
      </DashboardOverviewProvider>
    </div>
  );
}